import { screenLg, screenMd, screenSm } from 'constants/styles/breakpoints';
import styled, { createGlobalStyle } from 'styled-components';
import { Row } from 'components/Table';
import { CONTENT_TYPE_COLORS } from 'constants/playlists';

export const HeaderRow = styled(Row)`
  ${screenSm} {
    padding-left: 10px;
    padding-right: 10px;
  }
  ${screenLg} {
    padding-left: 10px;
    padding-right: 10px;
  }
`;

export const Container = styled.div<{ lock: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: 140px;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;

  ${screenMd} {
    padding-bottom: 0;
  }

  ${({ lock }) =>
    lock &&
    `
		opacity: 0.4;
		pointer-events: none;
	`}
`;

export const UsersRowContainer = styled.div<{ embeddedContent?: boolean }>`
  background: #ffffff;
  box-shadow: 0px 5px 20px rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  margin-bottom: 10px;
  ${({ embeddedContent }) =>
    embeddedContent &&
    `
    background: transparent;
    box-shadow: none;
    border-radius: 0;
    margin-bottom: 0;
  `}
  li:last-child {
    border-bottom: 1px solid #f2f2f7;
  }
  &:last-child li {
    border-bottom: none;
  }
`;

export const UserRow = styled.div<{
  disabled?: boolean;
  columns: string;
  transparent?: boolean;
  top?: boolean;
  topSelect?: boolean;
}>`
  height: 70px;
  align-items: center;
  padding: 0 10px;
  box-shadow: 0px 5px 20px rgba(0, 0, 0, 0.02);

  ${screenSm} {
    background: transparent;
    padding: 10px;
    margin: 0;
  }

  ${({ transparent }) =>
    transparent &&
    `
    background: transparent;
  `}

  ${({ disabled }) =>
    disabled &&
    `
    background: #f2f2f74a !important;
  `}

  ${({ top }) =>
    top &&
    `
    height: 56px;
    ${screenSm} {
      height: 45px;
      border: none;
    }
  `}

  ${({ topSelect }) =>
    topSelect &&
    `
    height: auto;
    padding: 4px;
    width: 200px;
  `}

  display: grid;
  grid-template-columns: ${({ columns }) => columns};
`;

export const ProgressContainer = styled.div<{
  visible: boolean;
  disabled?: boolean;
  transparent?: boolean;
  top?: boolean;
  topSelect?: boolean;
}>`
  ${({ transparent }) =>
    transparent &&
    `
    background: transparent;
  `}

  ${({ disabled }) =>
    disabled &&
    `
    background: #f2f2f74a !important;
  `}

  ${({ top }) =>
    top &&
    `
    height: 56px;
    ${screenSm} {
      height: 45px;
      border: none;
    }
  `}

  ${({ topSelect }) =>
    topSelect &&
    `
    height: auto;
    padding: 4px;
    width: 200px;
  `}

  display: ${({ visible }) => (visible ? 'flex' : 'none')};
  flex-direction: column;
`;

export const PlaylistTopicContainer = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  text-overflow: ellipsis;
  align-items: center;

  padding: 20px;

  ${screenSm} {
    padding: 30px;
  }

  border-bottom: 1px solid #d0d2db;

  &:last-child {
    border-bottom: none;
    padding-bottom: none;
  }
`;

export const TopicTitle = styled.div`
  font-weight: 700;
  font-size: 20px;
  line-height: 26px;
  color: #1a2535;
  width: 100%;
`;

export const GroupTitleLearningPath = styled.div`
  font-weight: 450;
  font-size: 16px;
  line-height: 24px;
  color: #1a2535;
  width: 100%;
  margin-bottom: 10px;
`;

export const ContentList = styled.ul`
  padding: 0;
  margin: 20px 0 0 0;
  width: 100%;
`;

export const SkillCheckContentList = styled.ul`
  padding: 0;
  margin: 0;
  width: 100%;
`;

export const RowContent = styled.li<{ embeddedContent?: boolean; noBorder?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-bottom: 1px solid #f2f2f7;
  ${({ noBorder }) =>
    noBorder &&
    `
    border-bottom: none;
  `}
  box-sizing: border-box;

  ${screenSm} {
    gap: 5px;
    flex-direction: row;
    align-items: center;
  }
  &:last-child {
    border-bottom: none;
  }

  .content-type-tag-container {
    flex: none;
    margin-right: 5px;
  }

  padding: 30px 30px 30px 0;
  ${({ embeddedContent }) =>
    embeddedContent &&
    `
    padding: 10px 0;
  `}
`;

export const GroupItemTitle = styled.span`
  flex: 1;

  font-size: 16px;
  font-style: normal;
  font-weight: 450;
  line-height: 24px;
`;

// The left rule takes the colour of the parent row's content tag.
export const LearningPathTopicContainer = styled.div<{ $contentType: string }>`
  background: rgba(242, 242, 247, 0.3);
  border-left: 2px solid ${({ $contentType }) => CONTENT_TYPE_COLORS[$contentType]};
  padding: 10px 20px;

  ${screenSm} {
    padding: 10px 30px 10px 20px;
  }
`;

export const SkillCheckTopicContainer = styled.div<{ $contentType: string }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: calc(100% + 5px);
  background: rgba(242, 242, 247, 0.3);
  border-left: 2px solid ${({ $contentType }) => CONTENT_TYPE_COLORS[$contentType]};
  padding: 10px 20px;

  ${screenSm} {
    padding: 5px 5px 5px 30px;
  }
  > div {
    background: transparent;
  }
`;

export const DeadlineCellButton = styled.button`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;

  &:focus-visible {
    outline: 2px solid var(--purple-light);
    outline-offset: 2px;
    border-radius: 6px;
  }
`;

export const DeadlineText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const DeadlinePrimaryText = styled.span<{ $disableHover?: boolean }>`
  font-size: 14px;
  font-family: Lato;
  font-weight: 700;
  line-height: 20px;
  color: ${({ $disableHover }) =>
    $disableHover ? 'var(--black-dark)' : 'var(--secondary-purple)'};
  word-wrap: break-word;
  border: ${({ $disableHover }) => ($disableHover ? 'none' : '1px solid transparent')};
  border-radius: 3px;
  padding: ${({ $disableHover }) => ($disableHover ? '0' : '0 5px')};
  /* The hover pill's horizontal padding bleeds left so the text edge stays
     flush with the column's other rows and the header label. */
  margin-left: ${({ $disableHover }) => ($disableHover ? '0' : '-5px')};
  box-sizing: border-box;
  background: ${({ $disableHover }) => ($disableHover ? 'transparent' : 'initial')};

  ${({ $disableHover }) =>
    $disableHover
      ? `
        &:hover {
          background: transparent !important;
        }

        ${DeadlineCellButton}:hover & {
          background: transparent;
          border-color: transparent;
          text-decoration: none;
        }
      `
      : `
        display: inline-flex;
        align-items: center;
        gap: 10px;
        min-height: 20px;

        &:hover,
        ${DeadlineCellButton}:not([data-disable-hover='true']):hover & {
          background: #f2f2f7 !important;
          border-color: #d3c9fa !important;
          text-decoration: none;
        }
      `}
`;

export const DeadlineSecondaryRow = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 3px;
  width: 100%;
`;

export const DeadlineSecondaryText = styled.span`
  font-size: 12px;
  font-family: Lato;
  font-weight: 400;
  line-height: 14px;
  color: #57575c;
  letter-spacing: 0;
  word-wrap: break-word;
`;

export const DeadlineSecondaryDateText = styled.span`
  color: var(--black-dark);
`;

export const DeadlineOverdueDot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 11px;
  height: 11px;
  flex-shrink: 0;
  border-radius: 999px;
  border: 2px solid rgba(254, 109, 109, 0.3);
  box-sizing: border-box;

  &::after {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: #fe6d6d;
  }
`;

export const DeadlineFilterButton = styled.button<{ $active?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;

  svg {
    position: relative;
    z-index: 1;
    transition: color 0.2s ease;
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 20px;
    height: 20px;
    border: 1px solid transparent;
    border-radius: 4px;
    background: transparent;
    transform: translate(-50%, -50%);
    transition: width 0.2s ease, height 0.2s ease, background-color 0.2s ease,
      border-color 0.2s ease, border-radius 0.2s ease;
  }

  &:hover::before {
    width: 22px;
    height: 22px;
    background: var(--grey-lighter);
    border-color: var(--purple-light);
  }

  &:hover svg {
    color: var(--purple-light) !important;
  }

  &:focus-visible {
    outline: 2px solid var(--purple-light);
    outline-offset: 2px;
  }
  &:active::before {
    width: 20px;
    height: 20px;
    border-radius: 3px;
    background: #7965c6;
    border-color: transparent;
  }

  &:active svg {
    color: #ffffff !important;
  }

  ${({ $active }) =>
    $active &&
    `
      &::before {
        width: 20px;
        height: 20px;
        border-radius: 3px;
        background: #7965c6;
        border-color: transparent;
      }

      &:hover::before {
        width: 20px;
        height: 20px;
        background: #7965c6;
        border-color: transparent;
      }

      svg {
        color: #ffffff !important;
      }
    `}
`;
export const DeadlineFilterDropdownGlobalStyles = createGlobalStyle`
  .deadline-filter-dropdown {
    .ant-popover-arrow {
      display: none;
    }

    .ant-popover-inner {
      border-radius: 10px;
      box-shadow: 0 30px 48px rgba(0, 0, 0, 0.2);
    }

    .ant-popover-inner-content {
      padding: 0;
    }
  }
`;

export const DeadlineFilterDropdownCard = styled.div`
  width: 172px;
  padding: 10px;
  border-radius: 10px;
  background: #ffffff;
`;

export const DeadlineFilterOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;

  .ant-checkbox-wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 30px;
    margin: 0;
    padding: 5px 4px;
    border-radius: 4px;
    transition: background-color 0.2s ease;
  }

  .ant-checkbox-wrapper:hover,
  .ant-checkbox-wrapper:focus-within {
    background: #f2f2f7;
  }

  .ant-checkbox-wrapper > span:not(.ant-checkbox) {
    padding-left: 8px;
    color: #57575c;
    font-size: 14px;
    line-height: 20px;
  }
`;
